import dayjs from "dayjs";
import { Contacts, type GQL } from "@binale-tech/shared";

import type { AgentEntityRef } from "./runner/types";
import { getDateFormat } from "@banks/scripts/helpers/getDateFormat";
import type { MessageId } from "@domain/intl/useTranslation";
import { formatDefault } from "@domain/utils/currencyFormatter";

type Translate = (id: MessageId) => string;

/** The live data a detail label is built from; the hook reads the contexts, the download passes the same maps. */
export interface AgentLabelSources {
    documents: ReadonlyMap<string, Pick<GQL.IDocument, "fileName" | "documentNumber">>;
    records: ReadonlyMap<string, { num?: string; brutto?: number }>;
    bankTransactions: ReadonlyMap<string, Pick<GQL.IBankingTransaction, "counterpartyName" | "bookingDate" | "amount">>;
    kasseTransactions: ReadonlyMap<
        string,
        Pick<GQL.ICashRegisterTransaction, "text" | "belegfeld1" | "date" | "amount">
    >;
    contacts: ReadonlyMap<string, Contacts.Contact>;
}

const SEPARATOR = " · ";

const unnamed: Record<AgentEntityRef["kind"], MessageId> = {
    document: "app.agent.details.unnamed.document",
    record: "app.agent.details.unnamed.record",
    bankTransaction: "app.agent.details.unnamed.bankTransaction",
    kasseTransaction: "app.agent.details.unnamed.kasseTransaction",
    contact: "app.agent.details.unnamed.contact",
};

const formatDate = (date: string | null | undefined): string | undefined => {
    if (!date) {
        return undefined;
    }
    try {
        return dayjs(date, getDateFormat(date)).format("DD.MM.YYYY");
    } catch {
        // getDateFormat throws on unknown patterns; show the raw value
        return date;
    }
};

const formatAmount = (cents: number | null | undefined): string | undefined =>
    typeof cents === "number" ? `${formatDefault(cents)} €` : undefined;

const join = (...parts: (string | null | undefined)[]): string =>
    parts
        .map((p) => p?.trim())
        .filter(Boolean)
        .join(SEPARATOR);

/** The label parts of a present entity ("" when it has none), undefined when the entity is absent. */
const describeEntity = (ref: AgentEntityRef, s: AgentLabelSources): string | undefined => {
    switch (ref.kind) {
        case "document": {
            const doc = s.documents.get(ref.id);
            return doc && join(doc.fileName || doc.documentNumber);
        }
        case "record": {
            const record = s.records.get(ref.id);
            return record && join(record.num, formatAmount(record.brutto));
        }
        case "bankTransaction": {
            const tx = s.bankTransactions.get(ref.id);
            return tx && join(tx.counterpartyName, formatDate(tx.bookingDate), formatAmount(tx.amount));
        }
        case "kasseTransaction": {
            const tx = s.kasseTransactions.get(ref.id);
            return tx && join(tx.text || tx.belegfeld1, formatDate(tx.date), formatAmount(tx.amount));
        }
        case "contact": {
            const contact = s.contacts.get(ref.id);
            return contact && join(Contacts.getLabelName(contact));
        }
    }
};

/** Whether the entity is in the sources; the hook times the "wird geladen" state with it. */
export const isEntityPresent = (ref: AgentEntityRef, sources: AgentLabelSources): boolean =>
    describeEntity(ref, sources) !== undefined;

/**
 * The client's label of a detail entity. An absent entity reads "wird geladen" while `waiting`
 * (it may still arrive), else "nicht mehr vorhanden"; a present one without label fields gets a per-kind fallback.
 */
export const entityLabel = (
    ref: AgentEntityRef,
    sources: AgentLabelSources,
    t: Translate,
    waiting: boolean,
): string => {
    const text = describeEntity(ref, sources);
    if (text === undefined) {
        return t(waiting ? "app.agent.details.loading" : "app.agent.details.gone");
    }
    return text || t(unnamed[ref.kind]);
};
